import {
  FLAP_IMPULSE,
  GRAVITY,
  MAX_FALL_SPEED,
  PITCH_DEGREES_PER_UNIT,
  PITCH_MAX,
  PITCH_MIN,
  SIM_DT,
} from '../data/tuning';
import { CEILING_Y } from './player';

/** Serializable player state in logical units (GDD §5.2). `y` is the centre of the fuselage. */
export interface PlayerState {
  x: number;
  y: number;
  vy: number;
}

export interface PlayerInput {
  flap: boolean;
}

/**
 * One fixed step of the flight physics (GDD §5.3). Pure: returns a new state.
 *
 * 1. A flap sets vy to +9 u/s (it replaces vy).
 * 2. Gravity, limited to the maximum fall speed.
 * 3. Position with the trapezoidal rule, which is exact for constant acceleration: a flap from rest
 *    peaks at exactly 1.35 u after 18 ticks, as the GDD derives. Euler variants would not.
 *
 * 4. Ceiling: above `ceiling` the plane is held there with vy = 0. It is harmless (GDD §5.2); pass
 *    Infinity for a sandbox without ceiling.
 */
export function stepPlayer(
  player: PlayerState,
  input: PlayerInput,
  ceiling: number = CEILING_Y,
  dt: number = SIM_DT,
): PlayerState {
  const vy = input.flap ? FLAP_IMPULSE : player.vy;
  const vyNew = Math.max(vy - GRAVITY * dt, MAX_FALL_SPEED);
  const y = player.y + ((vy + vyNew) / 2) * dt;
  if (y > ceiling) return { x: player.x, y: ceiling, vy: 0 };
  return { x: player.x, y, vy: vyNew };
}

/** Visual pitch in degrees for the render (#53, #67). Never used by collisions. */
export function pitchDegrees(vy: number): number {
  return Math.min(Math.max(vy * PITCH_DEGREES_PER_UNIT, PITCH_MIN), PITCH_MAX);
}
